import { useEffect, useRef, useState } from "react";
import { eventBus } from "../lib/eventBus";
import { getAuthToken } from "../lib/api";

// ─── Types ───────────────────────────────────────────────────────
interface AlphaState {
  status: string;
  trading_mode: string;
  current_balance: number;
  total_pnl: number;
  win_rate: number;
  daily_trades: number;
  active_markets: number;
  circuit_breaker_open: boolean;
}

interface Signal {
  id: string;
  timestamp: string;
  source: string;
  title: string;
  text: string;
  sentiment_compound: number;
  sentiment_label: string;
  acted_upon: number;
}

interface Trade {
  id: string;
  timestamp: string;
  market_question: string;
  direction: string;
  entry_price: number;
  size_usdc: number;
  kelly_fraction: number;
  edge: number;
  pnl_usdc: number | null;
  status: string;
}

interface EquityPoint {
  timestamp: string;
  balance: number;
  pnl_cumulative: number;
}

// ─── Constants ───────────────────────────────────────────────────
const BG = "#050810";
const PANEL = "#070d14";
const BORDER = "#0d1f0d";
const GREEN = "#00ff88";
const AMBER = "#ffaa00";
const RED = "#ff3333";
const BLUE = "#3399ff";
const DIM = "#2a4a2a";
const MID = "#3a6a4a";
const LABEL = "#556677";
const FONT = "'Courier New', monospace";

const API_BASE = "/api/sorv-alpha";

// ─── Helpers ─────────────────────────────────────────────────────
function timeAgo(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function fmtUsd(n: number): string {
  return n >= 0 ? `+$${n.toFixed(2)}` : `-$${Math.abs(n).toFixed(2)}`;
}

function sourceTag(src: string): string {
  if (src.includes("bbc")) return "BBC";
  if (src.includes("sky")) return "SKY";
  if (src.includes("npr")) return "NPR";
  if (src.includes("guardian")) return "GDN";
  if (src.includes("jazeera")) return "AJ";
  if (src.includes("coindesk")) return "CD";
  if (src.includes("cointelegraph")) return "CT";
  if (src.includes("gdelt")) return "GDT";
  return "RSS";
}

// ─── Hook: usePolymarketData ─────────────────────────────────────
function usePolymarketData() {
  const [state, setState] = useState<AlphaState>({
    status: "initializing",
    trading_mode: "paper",
    current_balance: 1000,
    total_pnl: 0,
    win_rate: 0,
    daily_trades: 0,
    active_markets: 0,
    circuit_breaker_open: false,
  });
  const [signals, setSignals] = useState<Signal[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [equity, setEquity] = useState<EquityPoint[]>([]);
  const [flash, setFlash] = useState(0);

  // Initial fetch
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const token = getAuthToken();
        const headers: Record<string, string> = token
          ? { Authorization: `Bearer ${token}` }
          : {};
        const [sRes, tRes, sigRes, eqRes] = await Promise.all([
          fetch(API_BASE + "/state", { headers }),
          fetch(API_BASE + "/trades?limit=30", { headers }),
          fetch(API_BASE + "/signals?limit=20", { headers }),
          fetch(API_BASE + "/equity?hours=24", { headers }),
        ]);
        if (sRes.ok) setState(await sRes.json());
        if (tRes.ok) setTrades(await tRes.json());
        if (sigRes.ok) setSignals(await sigRes.json());
        if (eqRes.ok) setEquity(await eqRes.json());
      } catch {
        /* worker not running yet */
      }
    };
    fetchAll();
  }, []);

  // Subscribe to WebSocket updates
  useEffect(() => {
    const handler = (msg: { type: string; data: any }) => {
      if (msg.type !== "sorv_alpha_update") return;
      const d = msg.data;
      if (d.state) {
        const s = d.state;
        setState({
          status: s.circuit_breaker_open === "True" ? "paused" : "active",
          trading_mode: s.trading_mode || "paper",
          current_balance: parseFloat(s.current_balance || "1000"),
          total_pnl: parseFloat(s.total_pnl || "0"),
          win_rate: parseFloat(s.win_rate || "0"),
          daily_trades: parseInt(s.daily_trades || "0"),
          active_markets: parseInt(s.active_markets_count || "0"),
          circuit_breaker_open: s.circuit_breaker_open === "True",
        });
      }
      if (d.recentSignals?.length) setSignals(d.recentSignals);
      if (d.recentTrades?.length) setTrades(d.recentTrades);
      if (d.equityPoints?.length) setEquity(d.equityPoints);
      setFlash((f) => f + 1);
    };
    const unsub = eventBus.subscribe(handler as any);
    return unsub;
  }, []);

  return { state, signals, trades, equity, flash };
}

// ─── Canvas: Neural Topology (physics-based) ────────────────────
interface NNode {
  x: number; y: number;
  vx: number; vy: number;
  phase: number; pulseSpeed: number;
  radius: number; baseRadius: number;
  activation: number; // 0-1 extra brightness from signal
}
interface Particle {
  fromIdx: number; toIdx: number;
  t: number; speed: number; // t goes 0→1→0
  forward: boolean;
}
interface Ring {
  x: number; y: number;
  r: number; maxR: number;
  alpha: number;
}

function NeuralTopologyCanvas({ flash }: { flash: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<{
    nodes: NNode[];
    particles: Particle[];
    rings: Ring[];
    connFlash: Map<string, number>; // "i-j" -> remaining flash ms
    inited: boolean;
  }>({ nodes: [], particles: [], rings: [], connFlash: new Map(), inited: false });

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const W = canvas.width;
    const H = canvas.height;
    const st = stateRef.current;
    const CONN_DIST = 120;

    // Init nodes once
    if (!st.inited) {
      st.inited = true;
      for (let i = 0; i < 32; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 0.3 + Math.random() * 0.5;
        st.nodes.push({
          x: 10 + Math.random() * (W - 20),
          y: 10 + Math.random() * (H - 20),
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          phase: Math.random() * Math.PI * 2,
          pulseSpeed: (0.03 + Math.random() * 0.03), // 2-4s cycle
          radius: 0,
          baseRadius: 2 + Math.random() * 3,
          activation: 0,
        });
      }
      // Seed some particles
      for (let i = 0; i < 10; i++) {
        st.particles.push({
          fromIdx: Math.floor(Math.random() * 32),
          toIdx: Math.floor(Math.random() * 32),
          t: Math.random(),
          speed: 0.008 + Math.random() * 0.008,
          forward: true,
        });
      }
    }

    const { nodes, particles, rings, connFlash } = st;
    let animId = 0;

    const draw = () => {
      // Clear
      ctx.fillStyle = "#050810";
      ctx.fillRect(0, 0, W, H);

      // Update node physics
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        // Bounce off edges
        if (n.x < 5 || n.x > W - 5) { n.vx *= -1; n.x = Math.max(5, Math.min(W - 5, n.x)); }
        if (n.y < 5 || n.y > H - 5) { n.vy *= -1; n.y = Math.max(5, Math.min(H - 5, n.y)); }
        // Pulse
        n.phase += n.pulseSpeed;
        const pulse = 0.4 + 0.6 * ((Math.sin(n.phase) + 1) / 2);
        n.radius = n.baseRadius * (0.8 + 0.4 * pulse);
        // Decay activation
        n.activation *= 0.97;
      }

      // Draw connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i]!;
          const b = nodes[j]!;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONN_DIST) {
            const key = `${i}-${j}`;
            const flashVal = connFlash.get(key) || 0;
            const baseAlpha = (1 - dist / CONN_DIST) * 0.4;
            const alpha = Math.min(1, baseAlpha + flashVal * 0.6);
            ctx.strokeStyle = `rgba(0,255,136,${alpha})`;
            ctx.lineWidth = flashVal > 0 ? 1 : 0.5;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
            // Decay flash
            if (flashVal > 0) connFlash.set(key, flashVal * 0.96);
          }
        }
      }

      // Draw particles along connections
      for (const p of particles) {
        const from = nodes[p.fromIdx]!;
        const to = nodes[p.toIdx]!;
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > CONN_DIST) {
          // Connection broken — reassign
          p.fromIdx = Math.floor(Math.random() * nodes.length);
          p.toIdx = Math.floor(Math.random() * nodes.length);
          p.t = 0;
          p.forward = true;
          continue;
        }
        p.t += p.forward ? p.speed : -p.speed;
        if (p.t >= 1) { p.forward = false; p.t = 1; }
        if (p.t <= 0) { p.forward = true; p.t = 0; p.fromIdx = Math.floor(Math.random() * nodes.length); p.toIdx = Math.floor(Math.random() * nodes.length); }
        const px = from.x + dx * p.t;
        const py = from.y + dy * p.t;
        // Particle dot
        ctx.fillStyle = `rgba(0,255,136,0.8)`;
        ctx.beginPath();
        ctx.arc(px, py, 1, 0, Math.PI * 2);
        ctx.fill();
        // Short trail
        const trailT = p.t - (p.forward ? 0.05 : -0.05);
        if (trailT >= 0 && trailT <= 1) {
          const tx = from.x + dx * trailT;
          const ty = from.y + dy * trailT;
          ctx.fillStyle = `rgba(0,255,136,0.3)`;
          ctx.beginPath();
          ctx.arc(tx, ty, 0.8, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Draw rings (expanding from activated nodes)
      for (let i = rings.length - 1; i >= 0; i--) {
        const ring = rings[i]!;
        ring.r += 0.8;
        ring.alpha *= 0.96;
        if (ring.alpha < 0.02 || ring.r > ring.maxR) {
          rings.splice(i, 1);
          continue;
        }
        ctx.strokeStyle = `rgba(0,255,136,${ring.alpha})`;
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.arc(ring.x, ring.y, ring.r, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Draw nodes
      for (const n of nodes) {
        const pulse = 0.4 + 0.6 * ((Math.sin(n.phase) + 1) / 2);
        const brightness = Math.min(1, pulse + n.activation);
        // Glow for activated nodes
        if (n.activation > 0.1) {
          ctx.fillStyle = `rgba(0,255,136,${n.activation * 0.15})`;
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.radius * 3, 0, Math.PI * 2);
          ctx.fill();
        }
        // Node dot
        ctx.fillStyle = `rgba(0,255,136,${brightness})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.radius, 0, Math.PI * 2);
        ctx.fill();
        // Emit ring at peak pulse
        if (Math.abs(Math.sin(n.phase) - 1) < 0.02 && Math.random() < 0.3) {
          rings.push({ x: n.x, y: n.y, r: n.radius, maxR: 25, alpha: 0.3 });
        }
      }

      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animId);
  }, []);

  // Signal activation: light up 3-5 nodes + propagate to neighbors
  useEffect(() => {
    if (flash === 0) return;
    const st = stateRef.current;
    const { nodes, connFlash, rings } = st;
    if (!nodes.length) return;

    // Pick 3-5 random nodes to activate
    const count = 3 + Math.floor(Math.random() * 3);
    const activated = new Set<number>();
    for (let k = 0; k < count; k++) {
      activated.add(Math.floor(Math.random() * nodes.length));
    }

    // Activate primary nodes immediately
    for (const idx of activated) {
      const n = nodes[idx]!;
      n.activation = 1;
      rings.push({ x: n.x, y: n.y, r: n.radius, maxR: 40, alpha: 0.5 });
    }

    // Flash connections involving activated nodes
    for (const idx of activated) {
      for (let j = 0; j < nodes.length; j++) {
        if (j === idx) continue;
        const a = nodes[idx]!;
        const b = nodes[j]!;
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 120) {
          const key = idx < j ? `${idx}-${j}` : `${j}-${idx}`;
          connFlash.set(key, 1);
        }
      }
    }

    // Propagate to neighbors with delay
    setTimeout(() => {
      for (const idx of activated) {
        for (let j = 0; j < nodes.length; j++) {
          if (activated.has(j)) continue;
          const a = nodes[idx]!;
          const b = nodes[j]!;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          if (Math.sqrt(dx * dx + dy * dy) < 120) {
            b.activation = Math.max(b.activation, 0.6);
          }
        }
      }
    }, 100);
  }, [flash]);

  return <canvas ref={ref} width={640} height={400} style={{ width: "100%", height: "100%", display: "block" }} />;
}

// ─── Canvas: Cortex Grid ─────────────────────────────────────────
function CortexGridCanvas({ flash }: { flash: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const gridRef = useRef<number[][]>([]);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const cols = 14, rows = 10;
    const cw = canvas.width / cols, ch = canvas.height / rows;

    if (gridRef.current.length === 0) {
      for (let r = 0; r < rows; r++) {
        gridRef.current.push(new Array(cols).fill(0).map(() => Math.random() * 0.3));
      }
    }
    const grid = gridRef.current;
    let animId = 0;

    const draw = () => {
      ctx.fillStyle = PANEL;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          grid[r]![c]! += (Math.random() - 0.5) * 0.04;
          grid[r]![c] = Math.max(0, Math.min(1, grid[r]![c]!));
          grid[r]![c]! *= 0.97; // decay
          const a = grid[r]![c]!;
          ctx.fillStyle = `rgba(0,255,136,${a})`;
          ctx.fillRect(c * cw + 1, r * ch + 1, cw - 2, ch - 2);
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animId);
  }, []);

  useEffect(() => {
    if (flash === 0) return;
    const grid = gridRef.current;
    if (!grid.length) return;
    const cr = Math.floor(Math.random() * grid.length);
    const cc = Math.floor(Math.random() * (grid[0]?.length || 14));
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        const r = cr + dr, c = cc + dc;
        if (grid[r]?.[c] !== undefined) grid[r][c] = 0.8 + Math.random() * 0.2;
      }
    }
  }, [flash]);

  return <canvas ref={ref} width={280} height={120} style={{ width: "100%", height: "100%", display: "block" }} />;
}

// ─── Canvas: Equity Curve ────────────────────────────────────────
function EquityCanvas({ equity }: { equity: EquityPoint[] }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const W = canvas.width, H = canvas.height;

    ctx.fillStyle = PANEL;
    ctx.fillRect(0, 0, W, H);

    const pts = equity.length > 1 ? equity : [{ balance: 1000 }, { balance: 1000 }];
    const vals = pts.map((p) => p.balance);
    const min = Math.min(...vals) * 0.99;
    const max = Math.max(...vals) * 1.01;
    const range = max - min || 1;

    // Grid lines
    ctx.strokeStyle = BORDER;
    ctx.lineWidth = 0.5;
    for (let i = 0; i < 4; i++) {
      const y = (H * i) / 3;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(W, y);
      ctx.stroke();
    }

    // Line
    ctx.strokeStyle = GREEN;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let i = 0; i < vals.length; i++) {
      const x = (i / (vals.length - 1)) * W;
      const y = H - ((vals[i]! - min) / range) * H;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    // End dot
    const lastX = W;
    const lastY = H - ((vals[vals.length - 1]! - min) / range) * H;
    ctx.fillStyle = GREEN;
    ctx.beginPath();
    ctx.arc(lastX - 2, lastY, 3, 0, Math.PI * 2);
    ctx.fill();

    // Labels
    ctx.fillStyle = DIM;
    ctx.font = `9px ${FONT}`;
    ctx.fillText(`$${max.toFixed(0)}`, 2, 10);
    ctx.fillText(`$${min.toFixed(0)}`, 2, H - 2);
  }, [equity]);

  return <canvas ref={ref} width={300} height={120} style={{ width: "100%", height: "100%", display: "block" }} />;
}

// ─── Canvas: Gradient Flow Waveform ──────────────────────────────
function GradientFlowCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);
  const phaseRef = useRef(0);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const W = canvas.width, H = canvas.height;
    let animId = 0;

    const draw = () => {
      phaseRef.current += 0.03;
      ctx.fillStyle = PANEL;
      ctx.fillRect(0, 0, W, H);

      ctx.strokeStyle = `rgba(0,255,136,0.6)`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = 0; x < W; x++) {
        const y =
          H / 2 +
          Math.sin(x * 0.04 + phaseRef.current) * 15 +
          Math.sin(x * 0.02 + phaseRef.current * 1.5) * 10 +
          Math.sin(x * 0.08 + phaseRef.current * 0.7) * 5;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animId);
  }, []);

  return <canvas ref={ref} width={280} height={60} style={{ width: "100%", height: "100%", display: "block" }} />;
}

// ─── Canvas: Weight Distribution ─────────────────────────────────
function WeightsCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);
  const barsRef = useRef<number[]>([]);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const W = canvas.width, H = canvas.height;
    const n = 20;

    if (barsRef.current.length === 0) {
      for (let i = 0; i < n; i++) barsRef.current.push(Math.random());
    }
    let animId = 0;

    const draw = () => {
      ctx.fillStyle = PANEL;
      ctx.fillRect(0, 0, W, H);
      const barW = W / n;
      for (let i = 0; i < n; i++) {
        barsRef.current[i]! += (Math.random() - 0.5) * 0.02;
        barsRef.current[i] = Math.max(0.05, Math.min(1, barsRef.current[i]!));
        const h = barsRef.current[i]! * (H - 10);
        ctx.fillStyle = `rgba(0,255,136,${0.3 + barsRef.current[i]! * 0.5})`;
        ctx.fillRect(i * barW + 1, H - h, barW - 2, h);
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animId);
  }, []);

  return <canvas ref={ref} width={280} height={100} style={{ width: "100%", height: "100%", display: "block" }} />;
}

// ─── Canvas: Training Metrics ────────────────────────────────────
function MetricsCanvas({ winRate }: { winRate: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const accRef = useRef<number[]>([]);
  const lossRef = useRef<number[]>([]);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const W = canvas.width, H = canvas.height;
    const maxPts = 60;

    const interval = setInterval(() => {
      const target = winRate || 0;
      const lastAcc = accRef.current.length ? (accRef.current[accRef.current.length - 1] ?? 0) : 0;
      const newAcc = lastAcc + (target - lastAcc) * 0.05 + (Math.random() - 0.5) * 0.02;
      accRef.current.push(Math.max(0, Math.min(1, newAcc)));
      if (accRef.current.length > maxPts) accRef.current.shift();

      const lastLoss = lossRef.current.length ? (lossRef.current[lossRef.current.length - 1] ?? 1) : 1;
      const newLoss = lastLoss - lastLoss * 0.03 + (Math.random() - 0.5) * 0.02;
      lossRef.current.push(Math.max(0.01, Math.min(1, newLoss)));
      if (lossRef.current.length > maxPts) lossRef.current.shift();

      // Draw
      ctx.fillStyle = PANEL;
      ctx.fillRect(0, 0, W, H);

      const drawLine = (pts: number[], color: string) => {
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let i = 0; i < pts.length; i++) {
          const x = (i / (maxPts - 1)) * W;
          const y = H - pts[i]! * H;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      };

      drawLine(accRef.current, GREEN);
      drawLine(lossRef.current, AMBER);

      ctx.fillStyle = DIM;
      ctx.font = `8px ${FONT}`;
      ctx.fillText("ACC", 2, 10);
      ctx.fillStyle = AMBER;
      ctx.fillText("LOSS", 2, 20);
    }, 500);

    return () => clearInterval(interval);
  }, [winRate]);

  return <canvas ref={ref} width={280} height={100} style={{ width: "100%", height: "100%", display: "block" }} />;
}

// ─── Panel: Layer Architecture ───────────────────────────────────
function LayerArchPanel({ state }: { state: AlphaState }) {
  const pnlPct =
    state.current_balance > 0
      ? ((state.total_pnl / (state.current_balance - state.total_pnl)) * 100).toFixed(1)
      : "0.0";

  const uptime = Math.floor((Date.now() - new Date().setHours(0, 0, 0, 0)) / 1000);
  const hh = Math.floor(uptime / 3600);
  const mm = Math.floor((uptime % 3600) / 60);

  return (
    <div style={{ padding: 12, fontSize: 11, fontFamily: FONT, color: MID }}>
      <div style={{ color: GREEN, fontSize: 10, letterSpacing: 2, marginBottom: 8 }}>
        LAYER ARCHITECTURE
      </div>
      <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 8 }}>
        <div style={{ color: GREEN, fontSize: 20, fontWeight: "bold" }}>
          ${state.current_balance.toFixed(2)}
        </div>
        <div style={{ color: state.total_pnl >= 0 ? GREEN : RED, fontSize: 13 }}>
          {fmtUsd(state.total_pnl)} ({pnlPct}%)
        </div>
      </div>
      <div style={{ marginTop: 12, lineHeight: 1.8 }}>
        {[
          ["active markets", String(state.active_markets)],
          ["win rate", `${(state.win_rate * 100).toFixed(1)}%`],
          ["daily trades", String(state.daily_trades)],
          ["mode", state.trading_mode.toUpperCase()],
          ["status", state.status.toUpperCase()],
          ["circuit brk", state.circuit_breaker_open ? "OPEN" : "OK"],
          ["uptime", `${hh}h ${mm}m`],
          ["model", "claude-s"],
        ].map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: LABEL }}>{k}</span>
            <span
              style={{
                color:
                  k === "circuit brk" && v === "OPEN"
                    ? RED
                    : k === "status" && v === "ACTIVE"
                      ? GREEN
                      : MID,
              }}
            >
              {v}
            </span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 16, color: GREEN, fontSize: 10, letterSpacing: 2 }}>
        ACTIVATION FUNCTIONS
      </div>
      <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 6, marginTop: 4, lineHeight: 1.8 }}>
        {[
          ["osint feed", "ACTIVE"],
          ["risk engine", "OK"],
          ["pm2 proc", "ONLINE"],
        ].map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: LABEL }}>{k}</span>
            <span style={{ color: GREEN }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Panel: OSINT Signal Feed ────────────────────────────────────
function OSINTFeedPanel({ signals }: { signals: Signal[] }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTop = 0;
  }, [signals.length]);

  if (!signals.length) {
    return (
      <div style={{ padding: 12, color: DIM, fontFamily: FONT, fontSize: 10 }}>
        COLLECTING OSINT SIGNALS...
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ padding: 8, overflowY: "auto", maxHeight: "100%" }}>
      {signals.map((s) => (
        <div
          key={s.id}
          style={{
            borderBottom: `1px solid ${BORDER}`,
            padding: "6px 0",
            fontFamily: FONT,
            fontSize: 10,
          }}
        >
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 2 }}>
            <span
              style={{
                background: BORDER,
                color: GREEN,
                padding: "1px 4px",
                fontSize: 8,
                letterSpacing: 1,
              }}
            >
              {sourceTag(s.source)}
            </span>
            <span
              style={{
                color:
                  s.sentiment_label === "positive"
                    ? GREEN
                    : s.sentiment_label === "negative"
                      ? RED
                      : LABEL,
                fontSize: 8,
              }}
            >
              {s.sentiment_label?.toUpperCase()}
            </span>
            <span style={{ color: s.sentiment_compound >= 0 ? GREEN : RED, fontSize: 9 }}>
              {s.sentiment_compound >= 0 ? "+" : ""}
              {s.sentiment_compound?.toFixed(2)}
            </span>
            {s.acted_upon === 1 && (
              <span style={{ color: AMBER, fontSize: 8 }}>[TRADED]</span>
            )}
            <span style={{ color: LABEL, fontSize: 8, marginLeft: "auto" }}>
              {timeAgo(s.timestamp)}
            </span>
          </div>
          <div style={{ color: MID, lineHeight: 1.3 }}>{s.title?.slice(0, 70)}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Panel: Trades Log ───────────────────────────────────────────
function TradesLogPanel({ trades }: { trades: Trade[] }) {
  const containerRef = useRef<HTMLDivElement>(null);

  if (!trades.length) {
    return (
      <div style={{ padding: 12, color: DIM, fontFamily: FONT, fontSize: 10 }}>
        PAPER MODE — AWAITING SIGNALS...
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ padding: 8, overflowY: "auto", maxHeight: "100%", fontFamily: FONT, fontSize: 10 }}>
      {trades.map((t) => {
        const time = new Date(t.timestamp).toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
        const pnl = t.pnl_usdc;
        const hasPnl = pnl !== null && pnl !== undefined;
        return (
          <div
            key={t.id}
            style={{
              display: "flex",
              gap: 6,
              padding: "4px 0",
              borderBottom: `1px solid ${BORDER}`,
              alignItems: "center",
            }}
          >
            <span style={{ color: LABEL, minWidth: 55 }}>{time}</span>
            <span style={{ color: MID, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {t.market_question?.slice(0, 30)}
            </span>
            <span style={{ color: t.direction === "YES" ? GREEN : RED, minWidth: 28 }}>
              {t.direction}
            </span>
            <span
              style={{
                color: hasPnl ? (pnl! >= 0 ? GREEN : RED) : LABEL,
                minWidth: 55,
                textAlign: "right",
              }}
            >
              {hasPnl ? fmtUsd(pnl!) : `$${t.size_usdc.toFixed(2)}`}
            </span>
            <span style={{ color: DIM, minWidth: 42, textAlign: "right" }}>
              k={t.kelly_fraction?.toFixed(2) || "0.00"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Panel: Training Log (simulated) ─────────────────────────────
function TrainingLogPanel({ state, flash: _flash }: { state: AlphaState; flash: number }) {
  const [lines, setLines] = useState<string[]>([
    "[INIT] sorv-alpha v1.0 online",
    "[MODE] paper trading active",
  ]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const templates = [
      () => `[WEIGHT_UPDATE] layer3 delta ${(Math.random() * 0.05).toFixed(3)}`,
      () => `[BACKPROP] gradient norm ${(1 + Math.random() * 2).toFixed(1)}`,
      () => `[OSINT_GATE] confidence ${(state.win_rate * 100).toFixed(0)}%`,
      () => `[SOFTMAX] output normalized ${(Math.random()).toFixed(3)}`,
      () => `[ARB_DETECT] edge ${(Math.random() * 0.15).toFixed(2)} delta`,
      () => `[BATCH] signals=${state.daily_trades} markets=${state.active_markets}`,
      () => `[RISK_CHK] daily_pnl ${fmtUsd(state.total_pnl)}`,
    ];

    const interval = setInterval(() => {
      const fn = templates[Math.floor(Math.random() * templates.length)];
      setLines((prev) => {
        const next = [...prev, fn!()];
        return next.length > 30 ? next.slice(-30) : next;
      });
    }, 3000 + Math.random() * 2000);

    return () => clearInterval(interval);
  }, [state.win_rate, state.total_pnl, state.daily_trades, state.active_markets]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [lines.length]);

  return (
    <div
      ref={containerRef}
      style={{
        padding: 8,
        fontFamily: FONT,
        fontSize: 9,
        color: DIM,
        overflowY: "auto",
        maxHeight: "100%",
        lineHeight: 1.6,
      }}
    >
      {lines.map((l, i) => (
        <div key={i}>
          {l.split(/(\[.*?\])/).map((part, j) =>
            part.startsWith("[") ? (
              <span key={j} style={{ color: GREEN }}>
                {part}
              </span>
            ) : (
              <span key={j}>{part}</span>
            )
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Topbar ──────────────────────────────────────────────────────
function Topbar({ state }: { state: AlphaState }) {
  const [utc, setUtc] = useState("");

  useEffect(() => {
    const tick = () => {
      setUtc(new Date().toISOString().slice(11, 19));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 16px",
        borderBottom: `1px solid ${BORDER}`,
        fontFamily: FONT,
        fontSize: 11,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ color: GREEN, letterSpacing: 3, fontWeight: "bold", fontSize: 12 }}>
          SORV-ALPHA
        </span>
        <span style={{ color: DIM }}>// NEURAL TRADING ENGINE</span>
      </div>
      <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
        <span
          style={{
            color: state.status === "active" ? GREEN : state.status === "paused" ? AMBER : RED,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: state.status === "active" ? GREEN : AMBER,
              display: "inline-block",
              boxShadow: state.status === "active" ? `0 0 6px ${GREEN}` : undefined,
            }}
          />
          {state.trading_mode.toUpperCase()}
        </span>
        <span style={{ color: BLUE, letterSpacing: 1 }}>{utc} UTC</span>
      </div>
    </div>
  );
}

// ─── Footer ──────────────────────────────────────────────────────
function Footer({ state }: { state: AlphaState }) {
  return (
    <div
      style={{
        padding: "6px 16px",
        borderTop: `1px solid ${BORDER}`,
        fontFamily: FONT,
        fontSize: 9,
        color: DIM,
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <span>SORV-ALPHA v1.0 // mode={state.trading_mode.toUpperCase()}</span>
      <span>openclaw_1@72.60.172.127</span>
    </div>
  );
}

// ─── Section Wrapper ─────────────────────────────────────────────
function Section({
  title,
  children,
  style: extraStyle,
}: {
  title: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        border: `1px solid ${BORDER}`,
        background: PANEL,
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        ...extraStyle,
      }}
    >
      <div
        style={{
          padding: "4px 8px",
          fontSize: 9,
          fontFamily: FONT,
          color: GREEN,
          letterSpacing: 2,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        {title}
      </div>
      <div style={{ flex: 1, overflow: "hidden" }}>{children}</div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────
export default function Polymarket() {
  const { state, signals, trades, equity, flash } = usePolymarketData();

  return (
    <div
      lang="en"
      translate="no"
      className="notranslate"
      style={{
        background: BG,
        color: MID,
        fontFamily: FONT,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Topbar state={state} />

      <div style={{ flex: 1, display: "grid", gridTemplateRows: "55% 45%", gap: 4, padding: 4, overflow: "hidden" }}>
        {/* ── Top row: metrics | canvases | right panels ── */}
        <div style={{ display: "grid", gridTemplateColumns: "190px 1fr 240px", gap: 4, minHeight: 0 }}>
          {/* Left column: Layer Architecture */}
          <Section title="LAYER ARCH" style={{ overflowY: "auto" }}>
            <LayerArchPanel state={state} />
          </Section>

          {/* Center column: Neural topology + P&L + Metrics */}
          <div style={{ display: "grid", gridTemplateRows: "55% 45%", gap: 4, minHeight: 0 }}>
            <Section title="NEURAL TOPOLOGY" style={{ minHeight: 0 }}>
              <NeuralTopologyCanvas flash={flash} />
            </Section>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, minHeight: 0 }}>
              <Section title="P&L CURVE" style={{ minHeight: 0 }}>
                <EquityCanvas equity={equity} />
              </Section>
              <Section title="TRAIN METRICS" style={{ minHeight: 0 }}>
                <MetricsCanvas winRate={state.win_rate} />
              </Section>
            </div>
          </div>

          {/* Right column: Weights + Gradient + Cortex + Log */}
          <div style={{ display: "grid", gridTemplateRows: "25% 15% 25% 35%", gap: 4, minHeight: 0 }}>
            <Section title="WEIGHT DIST" style={{ minHeight: 0 }}>
              <WeightsCanvas />
            </Section>
            <Section title="GRADIENT FLOW" style={{ minHeight: 0 }}>
              <GradientFlowCanvas />
            </Section>
            <Section title="CORTICAL ACTIVITY" style={{ minHeight: 0 }}>
              <CortexGridCanvas flash={flash} />
            </Section>
            <Section title="TRAINING LOG" style={{ minHeight: 0 }}>
              <TrainingLogPanel state={state} flash={flash} />
            </Section>
          </div>
        </div>

        {/* ── Bottom row: 3 equal columns ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, minHeight: 0 }}>
          <Section title="OSINT SIGNALS" style={{ minHeight: 0 }}>
            <OSINTFeedPanel signals={signals} />
          </Section>
          <Section title="TRADES LOG" style={{ minHeight: 0 }}>
            <TradesLogPanel trades={trades} />
          </Section>
          <Section title="ARB ENGINE LOG" style={{ minHeight: 0 }}>
            <TrainingLogPanel state={state} flash={flash} />
          </Section>
        </div>
      </div>

      <Footer state={state} />
    </div>
  );
}
